import { faqTopics } from './routes/faq';

export const publicSiteOrigin = 'https://www.trinirewards.com';
export const publicOgImageUrl = `${publicSiteOrigin}/og/trini-rewards-og.png`;

export type PublicSeoPath = '/' | '/how-it-works' | '/rewards' | '/about' | '/faq';

export type PublicSeoPage = {
  description: string;
  keyCopy: string;
  path: PublicSeoPath;
  title: string;
};

export const publicSeoPages: PublicSeoPage[] = [
  {
    description:
      'Take paid online surveys in Trinidad and Tobago and get paid in Digicel or bMobile airtime. Free to join. Run by Caribbean Market Research since 1970.',
    keyCopy: 'Take paid online surveys in Trinidad and Tobago and earn Digicel or bMobile airtime.',
    path: '/',
    title: 'Paid Online Surveys in Trinidad and Tobago, Earn Airtime | Trini Rewards',
  },
  {
    description:
      'Join free, take short online surveys that match you, earn Trini Points and cash out from TT$25 as Digicel or bMobile airtime. Here is how it works.',
    keyCopy:
      'A research panel is a group of people who share opinions for paid studies. Trini Rewards keeps the flow simple: join, verify, answer, earn, redeem.',
    path: '/how-it-works',
    title: 'How Trini Rewards Works, Get Paid in Airtime | Trinidad and Tobago',
  },
  {
    description:
      'See exactly what your Trini Points are worth. Cash out from TT$25 as prepaid Digicel or bMobile airtime, sent straight to your phone. No fees, clear values.',
    keyCopy:
      'One simple launch reward: mobile airtime for your Trinidad and Tobago number. Minimum cash-out is TT$25.00, with the exact airtime value confirmed before you send.',
    path: '/rewards',
    title: 'Rewards, Cash Out Surveys for Mobile Airtime | Trini Rewards',
  },
  {
    description:
      'Trini Rewards is an opt-in online survey panel for Trinidad and Tobago, run by Caribbean Market Research (est. 1970, ESOMAR member). Paid in airtime.',
    keyCopy:
      'Trini Rewards is a homegrown opinion panel connecting people in Trinidad and Tobago with brands, researchers, and public teams who need to understand this market properly.',
    path: '/about',
    title: 'About Trini Rewards, a Trinidad and Tobago Survey Panel | Run by CMR',
  },
  {
    description:
      'Answers on getting paid in airtime, the TT$25 cash-out floor, who can join, whether it is free, and whether paid surveys in Trinidad and Tobago are legitimate.',
    keyCopy: 'Adults (18+) living in Trinidad & Tobago.',
    path: '/faq',
    title: 'Trini Rewards FAQ, Are Paid Surveys Legit in Trinidad? | Trini Rewards',
  },
];

export const publicSeoPaths = publicSeoPages.map((page) => page.path);

export function normalizePublicPath(pathname: string): string {
  if (pathname === '/') {
    return '/';
  }

  return pathname.replace(/\/+$/, '');
}

export function getPublicSeoPage(pathname: string): PublicSeoPage | null {
  const normalized = normalizePublicPath(pathname);

  return publicSeoPages.find((page) => page.path === normalized) ?? null;
}

export function isPublicSeoPath(pathname: string): pathname is PublicSeoPath {
  return publicSeoPaths.includes(normalizePublicPath(pathname) as PublicSeoPath);
}

export function buildCanonicalUrl(pathname: PublicSeoPath): string {
  return pathname === '/' ? `${publicSiteOrigin}/` : `${publicSiteOrigin}${pathname}`;
}

export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    logo: publicOgImageUrl,
    name: 'Trini Rewards',
    parentOrganization: {
      '@type': 'Organization',
      name: 'CMR Panel LLC',
      parentOrganization: {
        '@type': 'Organization',
        name: 'Caribbean Market Research',
        url: 'https://caribbeanmarketresearch.com/',
      },
    },
    sameAs: ['https://cmr-panel.com/', 'https://caribbeanmarketresearch.com/'],
    url: `${publicSiteOrigin}/`,
  };
}

export function buildWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Trini Rewards',
    url: `${publicSiteOrigin}/`,
  };
}

export function buildFaqPageSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqTopics.flatMap((topic) =>
      topic.items.map((item) => ({
        '@type': 'Question',
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a,
        },
        name: item.q,
      })),
    ),
  };
}
